/**
 * Which lines on a document would push a product's stock below zero.
 *
 * Withdrawing (and selling) more than is on the shelf is allowed today and
 * nothing said a word about it -- that is how the test data ended up at
 * -12 on one product without anybody noticing. The shop's decision,
 * 2026-09-09: WARN, do not block. Going negative is deliberate for now,
 * because stock levels have not been entered yet.
 *
 * Later this becomes a shop-wide setting ("ขาย/เบิกสินค้าจนสต๊อกติดลบได้",
 * a checkbox in ตั้งค่า). `allowNegative` is that switch, already threaded
 * through so the setting only has to supply the value: `true` warns, and
 * `false` will refuse. It is not stored anywhere yet -- there is no
 * settings table in this database -- so callers pass `true` today.
 */

export interface StockLine {
    product: number;
    product_name?: string;
    quantity: number | string;
}

export interface ShortLine {
    product: number;
    product_name: string;
    /** Units on hand before this document. */
    inStock: number;
    /** Units this document takes. */
    wanted: number;
    /** How far below zero it lands, as a positive number. */
    short: number;
}

/** Stock on hand for a product id, or null when it is unknown. */
export type StockLookup = (productId: number) => number | null | undefined;

export const linesGoingNegative = (
    lines: StockLine[] | undefined,
    stockOf: StockLookup,
): ShortLine[] => {
    const out: ShortLine[] = [];

    (lines || []).forEach((line) => {
        const wanted = Number(line.quantity);
        if (!(wanted > 0)) return;

        const inStock = stockOf(line.product);
        // A product whose stock we do not know is not evidence of a
        // problem, so it is not reported as one.
        if (inStock === null || inStock === undefined || Number.isNaN(Number(inStock))) return;

        const remaining = Number(inStock) - wanted;
        if (remaining < 0) {
            out.push({
                product: line.product,
                product_name: line.product_name || String(line.product),
                inStock: Number(inStock),
                wanted,
                short: Math.abs(remaining),
            });
        }
    });

    return out;
};

/** "น้ำปลา (มี 2 เบิก 5 ติดลบ 3)", joined, trimmed to the first three. */
export const describeShortLines = (short: ShortLine[]): string => {
    const parts = short.map(
        (s) => `${s.product_name} (มี ${s.inStock} เบิก ${s.wanted} ติดลบ ${s.short})`,
    );
    return parts.slice(0, 3).join(', ') + (parts.length > 3 ? ` และอีก ${parts.length - 3} รายการ` : '');
};
